import type { LatLng } from './geo';
import { COUNTRY_BY_CODE, PLACES, type Continent, type Place, type ProviderWeights } from './places';
import { WORLD_SCALE_KM } from './scoring';

export type GameMapId = 'world' | 'europe' | 'france' | 'americas' | 'asia-oceania' | 'africa';

export interface GameMap {
  id: GameMapId;
  name: string;
  description: string;
  /** Distance scale used for scoring (≈ a tenth of the map's diagonal). */
  scaleKm: number;
  /** Initial view of the guess map. */
  view: { center: LatLng; zoom: number };
  includes: (place: Place) => boolean;
}

const inContinents = (...continents: Continent[]) => (place: Place) => continents.includes(place.continent);

export const GAME_MAPS: GameMap[] = [
  {
    id: 'world',
    name: 'Monde',
    description: 'Partout où il existe des images au niveau de la rue.',
    scaleKm: WORLD_SCALE_KM,
    view: { center: { lat: 25, lng: 10 }, zoom: 1 },
    includes: () => true,
  },
  {
    id: 'europe',
    name: 'Europe',
    description: 'Du Portugal à l’Oural, de la Laponie à la Méditerranée.',
    scaleKm: 500,
    view: { center: { lat: 52, lng: 14 }, zoom: 3 },
    includes: inContinents('europe'),
  },
  {
    id: 'france',
    name: 'France',
    description: 'La France métropolitaine et la Corse.',
    scaleKm: 120,
    view: { center: { lat: 46.6, lng: 2.4 }, zoom: 5 },
    includes: (place) => place.country === 'FR',
  },
  {
    id: 'americas',
    name: 'Amériques',
    description: 'De l’Alaska à la Terre de Feu.',
    scaleKm: 1300,
    view: { center: { lat: 12, lng: -80 }, zoom: 2 },
    includes: inContinents('north-america', 'south-america'),
  },
  {
    id: 'asia-oceania',
    name: 'Asie & Océanie',
    description: 'De la Turquie au Japon, jusqu’en Nouvelle-Zélande.',
    scaleKm: 1300,
    view: { center: { lat: 15, lng: 110 }, zoom: 2 },
    includes: inContinents('asia', 'oceania'),
  },
  {
    id: 'africa',
    name: 'Afrique',
    description: 'Les pays africains couverts par les images de rue.',
    scaleKm: 800,
    view: { center: { lat: 0, lng: 20 }, zoom: 3 },
    includes: inContinents('africa'),
  },
];

export const GAME_MAP_BY_ID: Record<GameMapId, GameMap> = Object.fromEntries(GAME_MAPS.map((m) => [m.id, m])) as Record<
  GameMapId,
  GameMap
>;

export function getGameMap(id: string): GameMap {
  return GAME_MAP_BY_ID[id as GameMapId] ?? GAME_MAP_BY_ID.world;
}

export type WeightKey = keyof ProviderWeights;

/** Places of a map that a provider may cover, with the weight of their country. */
export function candidatePlaces(map: GameMap, weightKey: WeightKey): { place: Place; weight: number }[] {
  const byCountry = new Map<string, Place[]>();
  for (const place of PLACES) {
    if (!map.includes(place)) continue;
    const weight = COUNTRY_BY_CODE[place.country]?.weights[weightKey] ?? 0;
    if (weight <= 0) continue;
    const list = byCountry.get(place.country) ?? [];
    list.push(place);
    byCountry.set(place.country, list);
  }
  // Each country gets its weight, shared between its places: countries are not favoured
  // just because they have more search areas listed.
  const result: { place: Place; weight: number }[] = [];
  for (const [country, places] of byCountry) {
    const countryWeight = COUNTRY_BY_CODE[country].weights[weightKey];
    for (const place of places) result.push({ place, weight: countryWeight / places.length });
  }
  return result;
}

export function weightedPick<T>(items: { weight: number; value: T }[], rng: () => number = Math.random): T {
  if (items.length === 0) throw new Error('weightedPick: empty list');
  const total = items.reduce((sum, item) => sum + Math.max(0, item.weight), 0);
  let r = rng() * total;
  for (const item of items) {
    r -= Math.max(0, item.weight);
    if (r < 0) return item.value;
  }
  return items[items.length - 1].value;
}

/** Picks a random search area for a provider on a map. */
export function pickPlace(map: GameMap, weightKey: WeightKey, rng: () => number = Math.random): Place {
  const candidates = candidatePlaces(map, weightKey);
  if (candidates.length === 0) throw new Error(`Aucune zone de recherche pour la carte « ${map.name} ».`);
  return weightedPick(
    candidates.map((c) => ({ weight: c.weight, value: c.place })),
    rng,
  );
}
